import { useState } from "react";
import { MERCHANTS } from "../constants/merchants";

export default function MapPage({ user, t }) {
    const [selectedZone, setSelectedZone] = useState(null);
    const [selectedTag, setSelectedTag] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [tagFilter, setTagFilter] = useState("all"); // "all" | "online" | "offline"
    const [searchTag, setSearchTag] = useState("");

    // ── 商戶資料隔離 ────────────────────────────────────────
    const isAdmin = user.accountType === "admin";
    const userMerchant = isAdmin
        ? null
        : MERCHANTS.find((m) => m.id !== "developer" && user.email.includes(m.emailDomain));

    const merchantPrefix = userMerchant ? userMerchant.name.split(" ")[0] : null;

    const allZones = [
        { id: "A", label: "區域 A — 飲品區", x: 40, y: 60, w: 160, h: 120, color: "#2a9d8f", products: ["橙汁 500ml (JCE-033)", "牛奶 1L (MLK-001)"] },
        { id: "B", label: "區域 B — 麵包烘焙", x: 240, y: 60, w: 140, h: 100, color: "#1a3a5c", products: ["全麥麵包 (BRD-021)"] },
        { id: "C", label: "區域 C — 零食區", x: 420, y: 60, w: 150, h: 130, color: "#e63946", products: ["薯片 Original (SNK-007)"] },
        { id: "D", label: "區域 D — 冷凍區", x: 40, y: 220, w: 530, h: 80, color: "#f77f00", products: ["冷凍食品 (待標記)"] },
    ];

    const allArucoTags = [
        { x: 120, y: 100, id: "TAG-001", zone: "A", status: "online", color: "#2a9d8f", signal: -62, lastSeen: "2 分鐘前" },
        { x: 310, y: 90, id: "TAG-002", zone: "B", status: "online", color: "#1a3a5c", signal: -55, lastSeen: "1 分鐘前" },
        { x: 495, y: 110, id: "TAG-003", zone: "C", status: "offline", color: "#e63946", signal: null, lastSeen: "3 小時前" },
        { x: 305, y: 250, id: "TAG-004", zone: "D", status: "online", color: "#f77f00", signal: -71, lastSeen: "5 分鐘前" },
    ];

    const filteredTags = allArucoTags.filter((tag) => {
        const matchSearch = tag.id.toLowerCase().includes(searchTag.toLowerCase());
        const matchStatus = tagFilter === "all" || tag.status === tagFilter;
        return matchSearch && matchStatus;
    });

    const zones = merchantPrefix
        ? allZones.filter((z) => z.id !== "C")
        : allZones;
    const arucoTags = merchantPrefix
        ? allArucoTags.filter((tag) => tag.id !== "TAG-003")
        : allArucoTags;

    const onlineCount = arucoTags.filter((t) => t.status === "online").length;
    const offlineCount = arucoTags.filter((t) => t.status === "offline").length;

    const shelfLines = [
        { x1: 60, y1: 80, x2: 180, y2: 80 },
        { x1: 60, y1: 100, x2: 180, y2: 100 },
        { x1: 60, y1: 120, x2: 180, y2: 120 },
        { x1: 255, y1: 80, x2: 365, y2: 80 },
        { x1: 255, y1: 100, x2: 365, y2: 100 },
        { x1: 440, y1: 80, x2: 550, y2: 80 },
        { x1: 440, y1: 105, x2: 550, y2: 105 },
        { x1: 440, y1: 130, x2: 550, y2: 130 },
    ];

    const cardStyle = { background: t.surface, borderRadius: 12, padding: 20, border: `1px solid ${t.border}` };

    return (
        <div style={{ padding: 32 }}>
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text, margin: "0 0 4px" }}>🗺️ 地圖系統</h2>
                        <p style={{ color: t.textFaint, fontSize: 13, margin: 0 }}>商品位置標記 · ArUco Tag 定位（使用 GeoJSON + PostGIS）</p>
                    </div>
                    {!isAdmin && userMerchant && (
                        <span style={{ color: t.success, fontSize: 11, padding: "4px 12px", background: `${t.success}18`, borderRadius: 20, border: `1px solid ${t.success}44`, fontWeight: 600 }}>
                            🔒 僅顯示 {userMerchant.name} 資料
                        </span>
                    )}
                </div>
            </div>

            {/* ── Status Bar ── */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                {[
                    { label: "總 Tag 數", value: arucoTags.length, color: t.accent },
                    { label: "在線", value: onlineCount, color: t.success },
                    { label: "離線", value: offlineCount, color: t.danger },
                    { label: "覆蓋區域", value: zones.length + " 區", color: "#f77f00" },
                ].map((stat) => (
                    <div key={stat.label} style={{
                        padding: "10px 18px", borderRadius: 10, background: `${stat.color}15`,
                        border: `1px solid ${stat.color}44`, display: "flex", alignItems: "center", gap: 8
                    }}>
                        <span style={{ color: stat.color, fontSize: 18, fontWeight: 800 }}>{stat.value}</span>
                        <span style={{ color: t.textFaint, fontSize: 12 }}>{stat.label}</span>
                    </div>
                ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>
                {/* ── Map Canvas ── */}
                <div style={cardStyle}>
                    {/* Toolbar */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                        <h3 style={{ color: t.text, fontSize: 15, fontWeight: 700, margin: 0 }}>📐 店面平面圖</h3>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {/* Zoom Controls */}
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <button
                                    onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                                    style={{ padding: "4px 10px", background: t.inputBg, border: `1px solid ${t.border}`, color: t.textMuted, borderRadius: 6, cursor: "pointer", fontSize: 14 }}>−</button>
                                <span style={{ fontSize: 12, color: t.textMuted, minWidth: 40, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                                <button
                                    onClick={() => setZoom((z) => Math.min(2, +(z + 0.25).toFixed(2)))}
                                    style={{ padding: "4px 10px", background: t.inputBg, border: `1px solid ${t.border}`, color: t.textMuted, borderRadius: 6, cursor: "pointer", fontSize: 14 }}>+</button>
                            </div>
                            {/* Filter */}
                            <div style={{ display: "flex", gap: 4 }}>
                                {[{ key: "all", label: "全部" }, { key: "online", label: "在線" }, { key: "offline", label: "離線" }].map((f) => (
                                    <button key={f.key}
                                        onClick={() => setTagFilter(f.key)}
                                        style={{
                                            padding: "5px 12px", borderRadius: 20, fontSize: 11, cursor: "pointer", fontWeight: 600,
                                            background: tagFilter === f.key ? t.accent : t.inputBg,
                                            border: `1px solid ${tagFilter === f.key ? t.accent : t.border}`,
                                            color: tagFilter === f.key ? "white" : t.textMuted
                                        }}>
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            {/* Reset */}
                            <button onClick={() => { setZoom(1); setSelectedZone(null); setSelectedTag(null); setTagFilter("all"); setSearchTag(""); }}
                                style={{ padding: "5px 12px", background: t.inputBg, border: `1px solid ${t.border}`, color: t.textMuted, borderRadius: 6, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                                🔄 重置
                            </button>
                        </div>
                    </div>

                    {/* SVG Map */}
                    <div style={{ background: t.inputBg, borderRadius: 10, border: `2px solid ${t.border}`, overflow: "hidden" }}>
                        <svg
                            width="100%"
                            viewBox="0 0 620 340"
                            style={{ display: "block", transform: `scale(${zoom})`, transformOrigin: "top left", transition: "transform 0.2s ease" }}
                        >
                            {/* Grid */}
                            {Array.from({ length: 13 }).map((_, i) => (
                                <line key={`v${i}`} x1={i * 50} y1={0} x2={i * 50} y2={340} stroke={t.border} strokeWidth={0.5} />
                            ))}
                            {Array.from({ length: 7 }).map((_, i) => (
                                <line key={`h${i}`} x1={0} y1={i * 50} x2={620} y2={i * 50} stroke={t.border} strokeWidth={0.5} />
                            ))}
                            {/* Outer Wall */}
                            <rect x={20} y={20} width={580} height={300} rx={8} fill="none" stroke={t.textMuted} strokeWidth={3} />
                            {/* Entrance */}
                            <rect x={270} y={17} width={80} height={6} fill={t.bg} />
                            <text x={310} y={14} textAnchor="middle" fill={t.textFaint} fontSize={10}>入口</text>
                            {/* Zones */}
                            {zones.map((z) => (
                                <g key={z.id} onClick={() => { setSelectedZone(selectedZone?.id === z.id ? null : z); setSelectedTag(null); }}
                                    style={{ cursor: "pointer" }}>
                                    <rect x={z.x} y={z.y} width={z.w} height={z.h} rx={6}
                                        fill={selectedZone?.id === z.id ? `${z.color}55` : `${z.color}22`}
                                        stroke={z.color}
                                        strokeWidth={selectedZone?.id === z.id ? 2.5 : 1.5}
                                        strokeDasharray={selectedZone?.id === z.id ? "none" : "6 3"} />
                                    <text x={z.x + z.w / 2} y={z.y + z.h / 2 - 6} textAnchor="middle" fill={z.color} fontSize={13} fontWeight="700">{z.id}</text>
                                    <text x={z.x + z.w / 2} y={z.y + z.h / 2 + 10} textAnchor="middle" fill={z.color} fontSize={9} opacity={0.85}>{z.label.split("—")[1]?.trim()}</text>
                                </g>
                            ))}
                            {/* ArUco Tags */}
                            {arucoTags.map((tag) => {
                                const isActive = selectedTag?.id === tag.id;
                                const isHidden = !filteredTags.find((t) => t.id === tag.id);
                                return (
                                    <g key={tag.id} onClick={() => { setSelectedTag(isActive ? null : tag); setSelectedZone(null); }}
                                        style={{ cursor: "pointer", opacity: isHidden ? 0.2 : 1, transition: "opacity 0.2s" }}>
                                        <circle cx={tag.x} cy={tag.y} r={isActive ? 18 : 14}
                                            fill={isActive ? `${tag.color}55` : `${tag.color}22`}
                                            stroke={tag.color} strokeWidth={isActive ? 2.5 : 1.5}
                                            strokeDasharray={tag.status === "offline" ? "4 2" : "none"} />
                                        <rect x={tag.x - 8} y={tag.y - 8} width={16} height={16} rx={3}
                                            fill={tag.color} opacity={0.9} />
                                        <rect x={tag.x - 5} y={tag.y - 5} width={10} height={10} rx={1}
                                            fill="white" opacity={0.4} />
                                        <text x={tag.x} y={tag.y + 22} textAnchor="middle"
                                            fill={t.textFaint} fontSize={8} fontWeight="600">{tag.id}</text>
                                    </g>
                                );
                            })}
                            {/* Shelf Lines */}
                            {shelfLines.map((l, i) => (
                                <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                                    stroke={t.textFaint} strokeWidth={1.5} opacity={0.5} />
                            ))}
                            {/* Legend */}
                            <g>
                                <circle cx={36} cy={315} r={5} fill={t.accent} opacity={0.8} />
                                <text x={46} y={319} fill={t.textFaint} fontSize={9}>ArUco Tag</text>
                                <rect x={120} y={310} width={14} height={10} rx={2} fill="none" stroke={t.textMuted} strokeWidth={1.5} strokeDasharray="4 2" />
                                <text x={140} y={319} fill={t.textFaint} fontSize={9}>商品區域</text>
                                <circle cx={220} cy={315} r={5} fill={t.success} opacity={0.8} />
                                <text x={230} y={319} fill={t.textFaint} fontSize={9}>在線</text>
                                <circle cx={270} cy={315} r={5} fill={t.danger} opacity={0.8} strokeDasharray="2 1" />
                                <text x={280} y={319} fill={t.textFaint} fontSize={9}>離線</text>
                            </g>
                        </svg>
                    </div>

                    {/* Badges */}
                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        {["GeoJSON 封包", "PostGIS 儲存", "ArUco Tag 定位", "2D 平面圖"].map((badge) => (
                            <span key={badge} style={{ fontSize: 11, padding: "3px 10px", background: t.accentBg, color: t.accentText, borderRadius: 20, border: `1px solid ${t.border}` }}>{badge}</span>
                        ))}
                    </div>
                </div>

                {/* ── Side Panel ── */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Zone / Tag Detail */}
                    <div style={{ background: t.surface, borderRadius: 12, padding: 18, border: `1px solid ${selectedZone ? selectedZone.color + "88" : selectedTag ? selectedTag.color + "88" : t.border}` }}>
                        <h3 style={{ color: t.text, fontSize: 14, fontWeight: 700, margin: "0 0 14px" }}>
                            {selectedTag ? `🏷️ ${selectedTag.id}` : selectedZone ? `📦 ${selectedZone.label}` : "📦 區域 / Tag 詳情"}
                        </h3>

                        {selectedTag ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {[
                                    { label: "Tag ID", value: selectedTag.id, mono: true },
                                    { label: "所在區域", value: `區域 ${selectedTag.zone}` },
                                    { label: "訊號強度", value: selectedTag.signal != null ? `${selectedTag.signal} dBm` : "無訊號" },
                                    { label: "最後上線", value: selectedTag.lastSeen },
                                    { label: "狀態", value: selectedTag.status === "online" ? "● 在線" : "○ 離線", status: selectedTag.status },
                                ].map((row) => (
                                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ color: t.textFaint, fontSize: 12 }}>{row.label}</span>
                                        <span style={{
                                            color: row.status === "online" ? t.success : row.status === "offline" ? t.danger : t.text,
                                            fontSize: 12, fontWeight: 600
                                        }}>{row.value}</span>
                                    </div>
                                ))}
                                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                                    <button style={{ flex: 1, padding: "7px", background: t.accent, border: "none", color: "white", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>✏️ 編輯 Tag</button>
                                    <button style={{ flex: 1, padding: "7px", background: "transparent", border: `1px solid ${t.border}`, color: t.textMuted, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>🔍 查看商品</button>
                                </div>
                            </div>
                        ) : selectedZone ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                                {[
                                    { label: "區域 ID", value: `區域 ${selectedZone.id}` },
                                    { label: "位置座標", value: `(${selectedZone.x}, ${selectedZone.y})` },
                                    { label: "尺寸", value: `${selectedZone.w} × ${selectedZone.h}` },
                                    { label: "商品數量", value: `${selectedZone.products.length} 種` },
                                ].map((row) => (
                                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ color: t.textFaint, fontSize: 12 }}>{row.label}</span>
                                        <span style={{ color: t.text, fontSize: 12, fontWeight: 600 }}>{row.value}</span>
                                    </div>
                                ))}
                                <p style={{ color: t.textMuted, fontSize: 12, fontWeight: 700, margin: "0 0 8px" }}>📋 標記商品：</p>
                                {selectedZone.products.map((prod) => (
                                    <div key={prod} style={{ padding: "6px 10px", background: t.inputBg, borderRadius: 6, marginBottom: 6, fontSize: 12, color: t.text }}>{prod}</div>
                                ))}
                                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                                    <button style={{ flex: 1, padding: "7px", background: t.accent, border: "none", color: "white", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>✏️ 編輯區域</button>
                                    <button style={{ flex: 1, padding: "7px", background: "transparent", border: `1px solid ${t.border}`, color: t.textMuted, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>📍 新增 Tag</button>
                                </div>
                            </div>
                        ) : (
                            <p style={{ color: t.textFaint, fontSize: 13, textAlign: "center", padding: "20px 0" }}>點擊地圖上的區域<br />或 Tag 查看詳細資訊</p>
                        )}
                    </div>

                    {/* Tag Search */}
                    <div style={{ background: t.surface, borderRadius: 12, padding: 18, border: `1px solid ${t.border}` }}>
                        <h3 style={{ color: t.text, fontSize: 14, fontWeight: 700, margin: "0 0 12px" }}>🔍 搜尋 Tag</h3>
                        <input
                            type="text"
                            value={searchTag}
                            onChange={(e) => setSearchTag(e.target.value)}
                            placeholder="輸入 Tag ID，如 TAG-001..."
                            style={{
                                background: t.inputBg, border: `1px solid ${t.border}`, color: t.text,
                                borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box"
                            }}
                        />
                        {searchTag && (
                            <p style={{ color: t.textFaint, fontSize: 11, margin: "6px 0 0" }}>
                                找到 {filteredTags.length} 個符合 "{searchTag}" 的 Tag
                            </p>
                        )}
                    </div>

                    {/* ArUco Tag List */}
                    <div style={{ background: t.surface, borderRadius: 12, padding: 18, border: `1px solid ${t.border}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <h3 style={{ color: t.text, fontSize: 14, fontWeight: 700, margin: 0 }}>🔖 ArUco Tag 列表</h3>
                            <span style={{ fontSize: 11, color: t.textFaint }}>{filteredTags.length} / {arucoTags.length}</span>
                        </div>
                        {filteredTags.length === 0 ? (
                            <p style={{ color: t.textFaint, fontSize: 12, textAlign: "center", padding: "12px 0" }}>沒有符合條件的 Tag</p>
                        ) : (
                            filteredTags.map((tag) => (
                                <div key={tag.id}
                                    onClick={() => setSelectedTag(tag)}
                                    style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "8px 10px", background: selectedTag?.id === tag.id ? `${tag.color}18` : t.inputBg,
                                        borderRadius: 6, marginBottom: 6, border: `1px solid ${selectedTag?.id === tag.id ? tag.color + "66" : t.border}`,
                                        cursor: "pointer", transition: "all 0.15s"
                                    }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: 2, background: tag.color }} />
                                        <span style={{ color: t.text, fontSize: 12, fontWeight: 600 }}>{tag.id}</span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ color: t.textFaint, fontSize: 11 }}>區域 {tag.zone}</span>
                                        <span style={{
                                            fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700,
                                            background: tag.status === "online" ? `${t.success}22` : `${t.danger}22`,
                                            color: tag.status === "online" ? t.success : t.danger
                                        }}>
                                            {tag.status === "online" ? "●" : "○"} {tag.status === "online" ? "在線" : "離線"}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
